import { useEffect, useState } from "react";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Modal, Button, SuccessNotice, FormField, TextInput } from "@a2z/ui";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  whatsappNumber: string | null;
};

const whatsappPattern = /^01\d{9}$/;

export function BrandingWhatsappSheet({
  isOpen,
  onClose,
  storeSlug,
  whatsappNumber,
}: Props) {
  const t = useTranslations();
  const { mutateAsync: updateStoreSettings, isPending: isSaving } =
    useUpdateStoreSettingsMutation();
  const [draftNumber, setDraftNumber] = useState(whatsappNumber ?? "");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seed the draft from the latest value each time the sheet opens
      setDraftNumber(whatsappNumber ?? "");
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, whatsappNumber]);

  const normalizedNumber = draftNumber.trim();
  const isDirty = normalizedNumber !== (whatsappNumber ?? "");

  const handleSave = async () => {
    setError(null);
    setSuccess(false);

    if (normalizedNumber.length > 0 && !whatsappPattern.test(normalizedNumber)) {
      setError(t(keys.seller.whatsappNumberInvalid));
      return;
    }

    try {
      await updateStoreSettings({
        storeSlug,
        whatsappNumber: normalizedNumber.length > 0 ? normalizedNumber : null,
      });
      setSuccess(true);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t(keys.seller.contactSectionTitle)}
      closeLabel={t(keys.common.cancel)}
      showFooterWhen={isDirty}
      footerActions={
        <Button
          onClick={() => void handleSave()}
          disabled={isSaving || !isDirty}
          variant="primary"
          size="md"
        >
          {isSaving ? t(keys.common.loading) : t(keys.common.save)}
        </Button>
      }
    >
      <p className="text-sm text-[color:var(--text-muted)]">
        {t(keys.seller.whatsappNumberHelper)}
      </p>

      {success ? (
        <SuccessNotice title={t(keys.seller.whatsappNumberSavedNotice)} />
      ) : null}

      <FormField
        name="whatsappNumber"
        label={t(keys.seller.whatsappNumberLabel)}
        error={error ?? undefined}
      >
        <TextInput
          name="whatsappNumber"
          inputMode="numeric"
          dir="ltr"
          placeholder={t(keys.seller.whatsappNumberPlaceholder)}
          value={draftNumber}
          hasError={Boolean(error)}
          onChange={(event) => {
            setSuccess(false);
            setError(null);
            setDraftNumber(event.target.value.replace(/\s+/g, ""));
          }}
        />
      </FormField>
    </Modal>
  );
}
